import { useCallback, useEffect, useRef } from "react"
import type React from "react"

interface UseDataGridVirtualScrollListenerOptions {
  gridRootRef: React.RefObject<HTMLDivElement | null>
  onScroll: (scrollTop: number) => void
  onWheelUpAtTop?: () => void
}

interface UseDataGridVirtualScrollListenerValue {
  adjustScrollTopBy: (scrollTopDelta: number) => void
}

export const useDataGridVirtualScrollListener = ({
  gridRootRef,
  onScroll,
  onWheelUpAtTop,
}: UseDataGridVirtualScrollListenerOptions): UseDataGridVirtualScrollListenerValue => {
  const virtualScrollerRef = useRef<HTMLElement | null>(null)
  const adjustScrollTopBy = useCallback((scrollTopDelta: number) => {
    if (!scrollTopDelta || !virtualScrollerRef.current) {
      return
    }
    virtualScrollerRef.current.scrollTop += scrollTopDelta
  }, [])

  useEffect(() => {
    const gridRoot = gridRootRef.current
    if (!gridRoot) {
      return
    }

    // Fallback: current MUI version lacks a stable top-scroll callback here.
    // Keep this localized selector until apiRef event support is reliable.
    const virtualScroller = gridRoot.querySelector<HTMLElement>(
      ".MuiDataGrid-virtualScroller"
    )
    if (!virtualScroller) {
      return
    }
    virtualScrollerRef.current = virtualScroller

    const handleVirtualScrollerScroll = () => {
      onScroll(virtualScroller.scrollTop)
    }
    const handleVirtualScrollerWheel = (event: WheelEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey) {
        return
      }

      const isWheelUp = event.deltaY < 0
      const isPinnedToTop = virtualScroller.scrollTop <= 1
      const isIntentionalUpwardScroll = event.deltaY <= -6
      if (
        onWheelUpAtTop &&
        isWheelUp &&
        isPinnedToTop &&
        isIntentionalUpwardScroll
      ) {
        onWheelUpAtTop()
      }
    }

    // Sync initial scroll position for infinite-scroll hooks that depend on
    // top deltas (e.g., loading previous pages when landing on page > 1).
    onScroll(virtualScroller.scrollTop)

    virtualScroller.addEventListener("scroll", handleVirtualScrollerScroll, {
      passive: true,
    })
    if (onWheelUpAtTop) {
      virtualScroller.addEventListener("wheel", handleVirtualScrollerWheel, {
        passive: true,
      })
    }

    return () => {
      virtualScroller.removeEventListener("scroll", handleVirtualScrollerScroll)
      if (onWheelUpAtTop) {
        virtualScroller.removeEventListener("wheel", handleVirtualScrollerWheel)
      }
      if (virtualScrollerRef.current === virtualScroller) {
        virtualScrollerRef.current = null
      }
    }
  }, [gridRootRef, onScroll, onWheelUpAtTop])

  return {
    adjustScrollTopBy,
  }
}
